// The agreement as a document to read (Brúnó, 2026-10-11: "wider, easier to
// read"). It arrives as plain text (domain/brand-contract.ts): blocks parted by
// a blank line, the first one the title, a numbered clause's first line its
// heading. Only the layout is added here; the words are exactly the ones the
// brand accepts, and the ones snapshotted with its acceptance. Since the final
// agreement (same day), a clause keeps its number ("2.1") in the margin, and a
// run of "- " lines, as in the "In short" summary, is a list.

import type {ReactNode} from 'react';

const NUMBERED = /^(\d+)\.\s+(.+)$/;
const CLAUSE = /^(\d+\.\d+)\s+(.+)$/;
const BULLET = /^[-•]\s+(.+)$/;
/** "Brave New, Inc., 131 Continental Drive … (the Platform)": the party and its role. */
const PARTY = /^(.*\S)\s+\((the [^)]+)\)$/;

function lines(block: string): string[] {
  return block.split('\n').map((line) => line.trim()).filter(Boolean);
}

/** A section's lines: clauses with their number in the margin, runs of bullets as one list. */
function Body({lines}: {lines: string[]}) {
  const out: ReactNode[] = [];
  for (let at = 0; at < lines.length; at += 1) {
    const line = lines[at] ?? '';
    if (BULLET.test(line)) {
      const items: string[] = [];
      for (; at < lines.length && BULLET.test(lines[at] ?? ''); at += 1) items.push(BULLET.exec(lines[at] ?? '')?.[1] ?? '');
      at -= 1;
      out.push(<ul className="agreement-list" key={at}>{items.map((item, n) => <li key={n}>{item}</li>)}</ul>);
      continue;
    }
    const clause = CLAUSE.exec(line);
    out.push(clause ? <p className="agreement-clause" key={at}><span>{clause[1]}</span><span>{clause[2]}</span></p> : <p key={at}>{line}</p>);
  }
  return <>{out}</>;
}

export function AgreementText({text}: {text: string}) {
  const blocks = text.trim().split(/\n{2,}/).map(lines).filter((block) => block.length);
  return <div className="agreement-doc">{blocks.map((block, index) => {
    const [first = '', ...rest] = block;
    if (index === 0) return <h2 key={index}>{block.join(' ')}</h2>;
    if (/^Date:/.test(first) && !rest.length) return <p className="agreement-meta" key={index}>{first}</p>;
    const numbered = NUMBERED.exec(first);
    // A short line without closing punctuation over more lines is a label, like "Parties".
    const label = !numbered && rest.length > 0 && first.length <= 60 && !/[.,;:]$/.test(first);
    if (!numbered && !label) {
      return <p className={index === blocks.length - 1 ? 'agreement-closing' : undefined} key={index}>{block.join(' ')}</p>;
    }
    const parties = label && rest.every((line) => PARTY.test(line));
    // A labelled block with bullets is a summary, like "In short": set apart from the clauses that bind.
    const summary = label && rest.some((line) => BULLET.test(line));
    return <section className={numbered ? 'agreement-numbered' : summary ? 'agreement-summary' : undefined} key={index}>
      <h3>{numbered ? <><span>{numbered[1]}.</span>{numbered[2]}</> : first}</h3>
      {parties ? <div className="agreement-parties">{rest.map((line) => {
        const [, who = line, role = ''] = PARTY.exec(line) ?? [];
        return <p className="agreement-party" key={line}><small>{role}</small>{who}</p>;
      })}</div> : <Body lines={rest} />}
    </section>;
  })}</div>;
}
